/**
 * Google Analytics utilities for Notability services
 *
 * This module provides Google Analytics 4 (GA4) integration alongside existing Mixpanel analytics.
 * It converts Mixpanel events to Google Analytics format with some expected fidelity loss.
 */

import type {GeneratedAnalyticsPropertyValue} from './analytics/generatedEventTypes'
import type {AnalyticsValue} from './utils/analytics'

export const GOOGLE_ANALYTICS_ID = 'AW-17106251811'

declare global {
  interface Window {
    gtag: (...args: any[]) => void
    dataLayer: any[]
  }
}

/**
 * Initialize Google Analytics 4
 * Should be called once during app initialization
 */
export function initGoogleAnalytics(): void {
  if ((window as any).gtag) {
    return // Already initialized
  }

  if (typeof window === 'undefined') {
    return // Server-side rendering
  }

  window.dataLayer = window.dataLayer || []

  window.gtag = function gtag() {
    window.dataLayer.push(arguments)
  }

  // Add GA4 script tag
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`
  document.head.appendChild(script)

  // Initialize GA4 with configuration
  window.gtag('js', new Date())
  window.gtag('config', GOOGLE_ANALYTICS_ID)
}

/**
 * Convert Mixpanel parameter names to GA4 format
 * GA4 requires snake_case and has naming restrictions
 */
function convertParameterName(mixpanelName: string): string {
  return mixpanelName
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .toLowerCase()
}

/**
 * Convert Mixpanel event name to GA4 format
 * GA4 custom events should be snake_case
 */
function convertEventName(mixpanelEventName: string): string {
  return mixpanelEventName
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .toLowerCase()
}

/**
 * Convert Mixpanel event to Google Analytics format
 * Note: Some fidelity may be lost due to GA4 parameter limits and naming conventions
 */
export function convertMixpanelToGA(
  eventName: string,
  parameters: Record<string, AnalyticsValue | GeneratedAnalyticsPropertyValue>
): {
  eventName: string
  parameters: Record<string, string | number | boolean | Date>
} {
  const gaEventName = convertEventName(eventName)
  const gaParameters: Record<string, string | number | boolean> = {}

  // Convert parameters, handling GA4 limitations
  let parameterCount = 0
  const maxParameters = 25 // GA4 limit

  for (const [key, value] of Object.entries(parameters)) {
    if (parameterCount >= maxParameters) {
      break // GA4 has a 25 parameter limit
    }

    if (value === null || value === undefined) {
      continue // Skip null/undefined values
    }

    const gaKey = convertParameterName(key)

    // Convert value to GA4 compatible type
    if (typeof value === 'string') {
      gaParameters[gaKey] = value
    } else if (typeof value === 'number') {
      gaParameters[gaKey] = value
    } else if (typeof value === 'boolean') {
      gaParameters[gaKey] = value
    } else if (value instanceof Date) {
      gaParameters[gaKey] = value.toISOString()
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // GA4 parameters are flat, so a nested map would only arrive as "[object Object]".
      continue
    } else {
      gaParameters[gaKey] = String(value)
    }

    parameterCount++
  }

  return {
    eventName: gaEventName,
    parameters: gaParameters
  }
}

/**
 * Track event to Google Analytics
 * This function safely handles GA4 tracking with error handling and returns a Promise
 * that resolves when the event has been processed or times out
 */
export function trackGoogleAnalyticsEvent(
  eventName: string,
  parameters: Record<string, AnalyticsValue | GeneratedAnalyticsPropertyValue> = {},
  timeoutMs: number = 2000
): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.gtag) {
      resolve() // Not available (SSR or not initialized)
      return
    }

    let fallbackTimer: ReturnType<typeof setTimeout> | undefined
    try {
      const {eventName: gaEventName, parameters: gaParameters} = convertMixpanelToGA(eventName, parameters)

      // event_timeout is honored only by the loaded GA library. When an ad blocker stops
      // the library, the gtag stub queues the event but no callback ever fires, so a
      // local timer has to resolve instead.
      fallbackTimer = setTimeout(resolve, timeoutMs)

      window.gtag('event', gaEventName, {
        ...gaParameters,
        event_callback: () => {
          clearTimeout(fallbackTimer)
          resolve()
        },
        event_timeout: timeoutMs
      })
    } catch (error) {
      clearTimeout(fallbackTimer)
      // Silently fail - we don't want GA4 errors to break the app
      console.warn('Google Analytics tracking failed:', error)
      resolve()
    }
  })
}
